"use client";

/**
 * FeedbackMock — animated mock of the askFinz Feedback portal for
 * /apps/feedback.
 *
 * Shows the two-panel layout: left sidebar (nav + app filter) and
 * main content (KPI row, tab bar, issue list).
 *
 * Animation loop (~7 s):
 *  1. Highlight the top P0 issue — vote ticks up by 1
 *  2. Tab filter switches to "Open"
 *  3. An issue status transitions open → verify with a flash label
 *  4. Reset and repeat
 */

import { useEffect, useRef, useState } from "react";
import {
  Bug,
  MessageSquare,
  ChevronUp,
  ChevronDown,
  ArrowUpRight,
  Flame,
  Sparkles,
} from "lucide-react";

type Sev = "P0" | "P1" | "P2" | "P3";
type IssueStatus = "open" | "progress" | "verify" | "resolved";

interface Issue {
  id: string;
  num: string;
  title: string;
  app: string;
  appColor: string;
  sev: Sev;
  status: IssueStatus;
  votes: number;
  comments: number;
  time: string;
}

const SEV_STYLE: Record<Sev, string> = {
  P0: "bg-red-500/15 text-red-400 border border-red-500/25",
  P1: "bg-orange-500/15 text-orange-400 border border-orange-500/20",
  P2: "bg-yellow-500/15 text-yellow-500 border border-yellow-500/20",
  P3: "bg-foreground/[0.06] text-foreground/65 border border-foreground/[0.10]",
};

const STATUS_STYLE: Record<IssueStatus, string> = {
  open:     "bg-blue-500/15 text-blue-400",
  progress: "bg-violet-500/15 text-violet-400",
  verify:   "bg-amber-500/15 text-amber-400",
  resolved: "bg-green-500/15 text-green-400",
};

const STATUS_LABEL: Record<IssueStatus, string> = {
  open: "Open",
  progress: "In progress",
  verify: "Verify",
  resolved: "Resolved",
};

const INITIAL_ISSUES: Issue[] = [
  {
    id: "i1", num: "BUG-038",
    title: "Flight search returns empty on multi-city routes",
    app: "Travel", appColor: "hsl(160 55% 42%)",
    sev: "P0", status: "open",
    votes: 47, comments: 12, time: "4h ago",
  },
  {
    id: "i2", num: "BUG-041",
    title: "Map satellite layer flickers on rapid zoom",
    app: "Map", appColor: "hsl(220 65% 52%)",
    sev: "P1", status: "open",
    votes: 24, comments: 5, time: "2h ago",
  },
  {
    id: "i3", num: "BUG-035",
    title: "Chat thread history not persisting after reload",
    app: "Chat", appColor: "hsl(280 55% 55%)",
    sev: "P2", status: "verify",
    votes: 19, comments: 3, time: "1d ago",
  },
  {
    id: "i4", num: "BUG-029",
    title: "Code editor loses cursor position in split pane",
    app: "Code", appColor: "hsl(30 65% 50%)",
    sev: "P2", status: "progress",
    votes: 11, comments: 2, time: "2d ago",
  },
];

const APP_CHIPS = [
  { label: "Travel", color: "hsl(160 55% 42%)" },
  { label: "Map",    color: "hsl(220 65% 52%)" },
  { label: "Chat",   color: "hsl(280 55% 55%)" },
  { label: "Code",   color: "hsl(30 65% 50%)" },
];

type Tab = "all" | "open" | "progress";

export function FeedbackMock() {
  const [issues, setIssues]             = useState<Issue[]>(INITIAL_ISSUES);
  const [tab, setTab]                   = useState<Tab>("all");
  const [highlightId, setHighlightId]   = useState<string | null>(null);
  const [flashedId, setFlashedId]       = useState<string | null>(null);
  const ref  = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const timers: number[] = [];
    const s = (fn: () => void, ms: number) => { timers.push(window.setTimeout(fn, ms)); };

    const runCycle = () => {
      setIssues(INITIAL_ISSUES);
      setTab("all");
      setHighlightId(null);
      setFlashedId(null);

      // 1. Highlight P0 issue
      s(() => setHighlightId("i1"), 700);

      // 2. Vote ticks up
      s(() => setIssues(p => p.map(i => i.id === "i1" ? { ...i, votes: 48 } : i)), 1300);

      // 3. Clear highlight, switch to Open tab
      s(() => { setHighlightId(null); setTab("open"); }, 2400);

      // 4. Highlight BUG-041, status changes open → verify
      s(() => {
        setHighlightId("i2");
        setFlashedId("i2");
        setIssues(p => p.map(i => i.id === "i2" ? { ...i, status: "verify" } : i));
      }, 3500);

      // 5. Clear, reset
      s(() => { setHighlightId(null); setFlashedId(null); }, 5000);
      s(() => { setTab("all"); }, 5600);

      // Loop
      s(runCycle, 7200);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => { obs.disconnect(); timers.forEach(clearTimeout); };
  }, []);

  const visible = tab === "all" ? issues : issues.filter(i => i.status === tab);

  const p0Open     = issues.filter(i => i.sev === "P0" && i.status !== "resolved").length;
  const openCount  = issues.filter(i => i.status === "open").length;
  const verifyCount = issues.filter(i => i.status === "verify").length;
  const resolvedCount = issues.filter(i => i.status === "resolved").length;

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">

        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            feedback.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            Demo
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[168px_1fr] gap-px bg-foreground/10 min-h-[440px]">

          {/* ── Left sidebar ── */}
          <div className="bg-background p-3 flex flex-col gap-1">
            <p className="px-2 py-1 text-[9px] tracking-[0.2em] uppercase text-foreground/35 font-mono">Feedback</p>
            <SideNavItem icon={Bug}           label="Issues"   count={issues.length} active />
            <SideNavItem icon={MessageSquare} label="Requests" count={8}             active={false} />

            <p className="mt-3 px-2 pb-0.5 text-[9px] tracking-[0.2em] uppercase text-foreground/30 font-mono">App filter</p>
            {APP_CHIPS.map(a => (
              <div key={a.label} className="flex items-center gap-2 px-2 py-[5px] rounded-md text-[10px] text-foreground/65 hover:text-foreground/70">
                <span className="h-2 w-2 rounded-[3px] flex-shrink-0" style={{ background: a.color }} />
                {a.label}
              </div>
            ))}

            <div className="mt-auto pt-4 border-t hairline">
              <button className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-foreground text-background text-[10px] font-medium px-3 py-1.5 tracking-wide">
                <Bug className="h-3 w-3" />
                Report issue
              </button>
            </div>
          </div>

          {/* ── Main panel ── */}
          <div className="bg-background flex flex-col">

            {/* KPI row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-foreground/10 border-b hairline">
              <KpiCell label="P0 OPEN"  value={String(p0Open)}      accent="text-red-400" />
              <KpiCell label="OPEN"     value={String(openCount)} />
              <KpiCell label="VERIFY"   value={String(verifyCount)}  accent="text-amber-400" />
              <KpiCell label="RESOLVED" value={String(resolvedCount)} accent="text-green-400" />
            </div>

            {/* Tab bar */}
            <div className="flex items-end gap-0.5 px-3 pt-2.5 border-b hairline overflow-x-auto shrink-0">
              {(["all", "open", "progress"] as const).map(t => (
                <button
                  key={t}
                  className={
                    "px-3 py-1.5 -mb-px text-[10px] tracking-[0.12em] uppercase font-mono border-b-2 transition-all " +
                    (tab === t
                      ? "border-foreground/80 text-foreground"
                      : "border-transparent text-foreground/65 hover:text-foreground/65")
                  }
                >
                  {t === "all" ? "All" : t === "open" ? "Open" : "In Progress"}
                  <span className="ml-1.5 text-foreground/30">
                    {t === "all" ? issues.length
                      : t === "open" ? issues.filter(i => i.status === "open").length
                      : issues.filter(i => i.status === "progress").length}
                  </span>
                </button>
              ))}
              <span className="ml-auto pb-1.5 text-[9px] font-mono text-foreground/30 flex items-center gap-1">
                <Flame className="h-2.5 w-2.5" /> Top
              </span>
            </div>

            {/* Issue rows */}
            <div className="divide-y divide-foreground/[0.055] overflow-auto">
              {visible.length === 0 ? (
                <div className="flex items-center justify-center py-10 text-[11px] text-foreground/35 font-mono">
                  No issues in this filter
                </div>
              ) : visible.map(issue => (
                <IssueRow
                  key={issue.id}
                  issue={issue}
                  highlight={highlightId === issue.id}
                  flashed={flashedId === issue.id}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer band */}
        <div className="border-t hairline bg-foreground/[0.02] px-5 py-2.5 flex items-center justify-between">
          <span className="text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono inline-flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" />
            Community-ranked · staff-triaged · hash-deduped
          </span>
          <span className="font-mono text-[10px] text-foreground/35">Updated just now</span>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <ArrowUpRight className="h-3 w-3" />
        <span>Live at <span className="font-mono text-foreground/65">feedback.askfinz.ai</span></span>
      </div>
    </div>
  );
}

/* ── Sub-components ── */

function SideNavItem({
  icon: Icon, label, count, active,
}: {
  icon: typeof Bug; label: string; count: number; active: boolean;
}) {
  return (
    <div className={
      "flex items-center gap-2 px-2 py-[5px] rounded-md text-[11px] " +
      (active ? "bg-foreground/10 text-foreground font-medium" : "text-foreground/65")
    }>
      <Icon className="h-3 w-3 flex-shrink-0" />
      <span className="flex-1">{label}</span>
      <span className={
        "text-[9px] font-mono px-1.5 py-0.5 rounded-full " +
        (active ? "bg-foreground/20 text-foreground/70" : "bg-foreground/[0.06] text-foreground/65")
      }>
        {count}
      </span>
    </div>
  );
}

function KpiCell({
  label, value, accent,
}: {
  label: string; value: string; accent?: string;
}) {
  return (
    <div className="bg-background px-3 py-2.5">
      <div className="text-[8px] tracking-[0.2em] uppercase text-foreground/35 font-mono">{label}</div>
      <div
        className={"mt-0.5 text-[22px] font-semibold tabular-nums tracking-tight leading-none " + (accent ?? "text-foreground")}
        style={{ transition: "all 500ms ease" }}
      >
        {value}
      </div>
    </div>
  );
}

function IssueRow({
  issue, highlight, flashed,
}: {
  issue: Issue; highlight: boolean; flashed: boolean;
}) {
  return (
    <div className={
      "flex items-center gap-3 px-3 py-2.5 transition-all duration-500 " +
      (highlight ? "bg-foreground/[0.035]" : "")
    }>
      {/* Vote widget */}
      <div className="flex flex-col items-center gap-px w-7 flex-shrink-0">
        <ChevronUp className="h-3 w-3 text-foreground/30" />
        <span
          className="text-[12px] font-mono font-semibold text-foreground/80 leading-none"
          style={{ transition: "all 350ms ease" }}
        >
          {issue.votes}
        </span>
        <ChevronDown className="h-3 w-3 text-foreground/20" />
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-medium text-foreground/88 truncate leading-snug">
          {issue.title}
        </div>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          <span className="text-[9px] font-mono text-foreground/30">{issue.num}</span>
          <span className="text-foreground/20 text-[9px]">·</span>
          <span className="inline-flex items-center gap-1 text-[9px] text-foreground/65">
            <span className="h-1.5 w-1.5 rounded-[2px]" style={{ background: issue.appColor }} />
            {issue.app}
          </span>
          <span className={"text-[9px] font-mono px-1.5 py-0.5 rounded " + SEV_STYLE[issue.sev]}>
            {issue.sev}
          </span>
          <span
            className={"text-[9px] px-1.5 py-0.5 rounded " + STATUS_STYLE[issue.status]}
            style={{ transition: "all 400ms ease" }}
          >
            {STATUS_LABEL[issue.status]}
          </span>
          {flashed && (
            <span className="text-[9px] text-amber-400 font-mono animate-[blur-fade-in_0.4s_ease-out]">
              ↑ status updated
            </span>
          )}
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-2 flex-shrink-0 text-foreground/30">
        <span className="flex items-center gap-1 text-[9px] font-mono">
          <MessageSquare className="h-2.5 w-2.5" />
          {issue.comments}
        </span>
        <span className="text-[9px] font-mono hidden sm:block">{issue.time}</span>
      </div>
    </div>
  );
}
